import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence, doc, getDoc, setDoc, collection, getDocs, Timestamp, deleteDoc, updateDoc, increment, query, orderBy, limit, runTransaction } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';
import type { ExtendedDonationRequest, TopDonor } from '../types';

const firebaseConfig = {
  apiKey: "AIzaSyBZPvFKdpfxhlbWfrFx91Y84H7kGrcnO1w",
  authDomain: "magrainev1.firebaseapp.com",
  databaseURL: "https://magrainev1-default-rtdb.firebaseio.com",
  projectId: "magrainev1",
  storageBucket: "magrainev1.firebasestorage.app",
  messagingSenderId: "298667316400",
  appId: "1:298667316400:web:8c7a9e9688d7ee0f48d775",
  measurementId: "G-LPDVQBYQV8"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const analytics = getAnalytics(app);

// Supprimez ou commentez la partie émulateur
// if (process.env.NODE_ENV === 'development') {
//   connectAuthEmulator(auth, 'http://localhost:9099');
//   connectFirestoreEmulator(db, 'localhost', 8080);
// }

// Déplacez enableIndexedDbPersistence après l'initialisation
try {
  enableIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn('Multiple tabs open, persistence can only be enabled in one tab at a time.');
    } else if (err.code === 'unimplemented') {
      console.warn('The current browser doesn\'t support persistence.');
    }
  });
} catch (error) {
  console.error('Erreur d\'initialisation de la persistence:', error);
}

// Initialize request price document
const initializeRequestPrice = async () => {
  const priceRef = doc(db, 'settings', 'requestPrice');
  const priceDoc = await getDoc(priceRef);
  
  if (!priceDoc.exists()) {
    await setDoc(priceRef, {
      amount: 500, // Prix initial en XAF
      lastUpdated: new Date()
    });
  }
};

// Initialize investment price
const initializeInvestmentPrice = async () => {
  const priceRef = doc(db, 'settings', 'investmentPrice');
  const priceDoc = await getDoc(priceRef);
  
  if (!priceDoc.exists()) {
    await setDoc(priceRef, {
      amount: 250, // Prix initial en XAF
      lastUpdated: new Date()
    });
  }
};

// Ajouter cette nouvelle fonction d'initialisation
const initializeCurrencySettings = async () => {
  console.log('Début de l\'initialisation de la devise...');
  try {
    const currencyRef = doc(db, 'settings', 'currency');
    console.log('Vérification de l\'existence du document currency...');
    
    const currencyDoc = await getDoc(currencyRef);
    
    if (!currencyDoc.exists()) {
      console.log('Document currency non trouvé, création en cours...');
      await setDoc(currencyRef, {
        code: 'FCFA',
        symbol: 'FCFA',
        lastUpdated: new Date(),
        isActive: true
      });
      console.log('Document currency créé avec succès !');
    } else {
      console.log('Document currency existe déjà:', currencyDoc.data());
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation de la devise:', error);
  }
};

// Initialiser le lien du service client
const initializeCustomerServiceLink = async () => {
  const linkRef = doc(db, 'settings', 'customerServiceLink');
  const linkDoc = await getDoc(linkRef);

  if (!linkDoc.exists()) {
    await setDoc(linkRef, {
      url: 'https://default-link.com', // Lien par défaut
      lastUpdated: new Date()
    });
  }
};

// Fonction pour récupérer le lien du service client
export const getCustomerServiceLink = async () => {
  const linkRef = doc(db, 'settings', 'customerServiceLink');
  const linkDoc = await getDoc(linkRef);

  if (linkDoc.exists()) {
    return linkDoc.data()?.url;
  }
  return null;
};

// Interface pour les lots
export interface Reward {
  id: string;
  title: string;
  imageUrl: string;
  price: number;
  isActive: boolean;
}

// Initialisation des lots par défaut
const initializeRewards = async () => {
  const rewardsRef = collection(db, 'rewards');
  const snapshot = await getDocs(rewardsRef);

  if (snapshot.empty) {
    // Lot par défaut
    const defaultReward = {
      title: 'Téléphone portable',
      imageUrl: '/phone-image.jpg',
      price: 50000,
      isActive: true,
      createdAt: new Date()
    };

    await setDoc(doc(rewardsRef, 'default'), defaultReward);
  }
};

// Fonction pour récupérer les lots actifs
export const getActiveRewards = async (): Promise<Reward[]> => {
  try {
    const rewardsRef = collection(db, 'rewards');
    const snapshot = await getDocs(rewardsRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Reward)).filter(reward => reward.isActive);
  } catch (error) {
    console.error('Erreur lors de la récupération des lots:', error);
    return [];
  }
};

// Constantes pour les collections et documents
export const REQUESTS_COLLECTION = '2dr_requests';
export const SETTINGS_COLLECTION = 'settings';
export const EXTENSION_PRICE_DOC = 'extensionPrice';

// Initialisation du prix de prolongation
const initializeExtensionPrice = async () => {
  const priceRef = doc(db, SETTINGS_COLLECTION, EXTENSION_PRICE_DOC);
  const priceDoc = await getDoc(priceRef);
  
  if (!priceDoc.exists()) {
    await setDoc(priceRef, {
      amount: 1000, // Prix initial en XAF
      lastUpdated: new Date()
    });
  }
};

// Fonction pour récupérer le prix de prolongation
export const getExtensionPrice = async (): Promise<number> => {
  try {
    const priceRef = doc(db, SETTINGS_COLLECTION, EXTENSION_PRICE_DOC);
    const priceDoc = await getDoc(priceRef);
    
    if (priceDoc.exists()) {
      return priceDoc.data().amount;
    }
    return 1000; // Prix par défaut
  } catch (error) {
    console.error('Erreur lors de la récupération du prix de prolongation:', error);
    return 1000; // Prix par défaut en cas d'erreur
  }
};

// Types pour les statistiques financières
export interface FinancialStats {
  totalBalance: number;        // Solde total de tous les portefeuilles
  totalWithdrawRequests: number; // Montant total des demandes de retrait en attente
  totalDeposits: number;      // Total des dépôts
  totalWithdraws: number;     // Total des retraits effectués
  lastUpdated: Date;
}

// Initialiser les statistiques financières
const initializeFinancialStats = async () => {
  try {
    const statsRef = doc(db, 'settings', 'financialStats');
    const statsDoc = await getDoc(statsRef);

    if (!statsDoc.exists()) {
      await setDoc(statsRef, {
        totalBalance: 0,
        totalWithdrawRequests: 0,
        totalDeposits: 0,
        totalWithdraws: 0,
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation des stats financières:', error);
  }
};

// Types pour l'état des services
export interface ServicesStatus {
  deposits: {
    active: boolean;
    message: string;
  };
  withdrawals: {
    active: boolean;
    message: string;
  };
  lastUpdated: Date;
}

// Initialiser l'état des services
const initializeServicesStatus = async () => {
  try {
    const statusRef = doc(db, 'settings', 'servicesStatus');
    const statusDoc = await getDoc(statusRef);

    if (!statusDoc.exists()) {
      await setDoc(statusRef, {
        deposits: {
          active: true,
          message: "Les recharges sont temporairement désactivées pour maintenance."
        },
        withdrawals: {
          active: true,
          message: "Les retraits sont temporairement désactivés pour maintenance."
        },
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du statut des services:', error);
  }
};

// Fonction pour récupérer l'état des services
export const getServicesStatus = async (): Promise<ServicesStatus | null> => {
  try {
    const statusRef = doc(db, 'settings', 'servicesStatus');
    const statusDoc = await getDoc(statusRef);
    
    if (statusDoc.exists()) {
      return {
        ...statusDoc.data(),
        lastUpdated: statusDoc.data().lastUpdated?.toDate()
      } as ServicesStatus;
    }
    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du statut des services:', error);
    return null;
  }
};

// Déclare d'abord la fonction
const initializeCollectiveInvestment = async () => {
  const investmentRef = doc(db, 'collectiveInvestment', 'current');
  const docSnap = await getDoc(investmentRef);
  
  if (!docSnap.exists()) {
    await setDoc(investmentRef, {
      totalAmount: 0,
      participantsCount: 0,
      participants: [],
      drawCount: 0,
      lastUpdated: new Date()
    });
  }
};

// Puis la fonction principale
const initializeFirebaseSettings = async () => {
  console.log('Démarrage des initialisations Firebase...');
  
  try {
    await initializeCollectiveInvestment();
    await initializeCurrencySettings();
    await initializeRequestPrice();
    await initializeInvestmentPrice();
    await initializeRewards();
    await initializeExtensionPrice();
    await initializeReferralSettings();
    await initializeFinancialStats();
    await initializeServicesStatus();
    await initializeGeneralDonation();
    
    // Importer et initialiser les numéros de paiement
    const { initializePaymentNumbers } = await import('../utils/transactionUtils');
    await initializePaymentNumbers();
    
    console.log('Toutes les initialisations sont terminées');
  } catch (error) {
    console.error('Erreur lors des initialisations:', error);
  }
};

// Appel explicite de l'initialisation
initializeFirebaseSettings().catch(console.error);

// Export de la fonction pour pouvoir la réutiliser si nécessaire
export { initializeFirebaseSettings };

// Appel de l'initialisation
initializeCustomerServiceLink().catch(console.error);

// Fonction pour récupérer le texte de la politique de confidentialité
export const getPrivacyPolicyContent = async () => {
  const docRef = doc(db, 'settings', 'privacyPolicy');
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data()?.content;
  }
  return null;
};

// Fonction pour récupérer le texte des termes d'utilisation
export const getTermsOfUseContent = async () => {
  const docRef = doc(db, 'settings', 'termsOfUse');
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data()?.content;
  }
  return null;
};

export const getTelegramLink = async () => {
  const docRef = doc(db, 'settings', 'telegramLink');
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data().url : null;
};

// Fonction pour récupérer les pays et les villes
export const getLocations = async () => {
  console.log('Début getLocations');
  const locationsRef = collection(db, 'locations');
  const snapshot = await getDocs(locationsRef);
  console.log('Documents trouvés:', snapshot.size);
  
  const locations = snapshot.docs.map(doc => {
    const data = doc.data();
    console.log('Document data:', data);
    const cities = data.cities.map((city: any) => city.name);
    console.log('Cities extracted:', cities);
    
    return {
      code: doc.id,
      name: data.name,
      phonePrefix: data.phonePrefix,
      cities: cities
    };
  });
  
  console.log('Locations finales:', locations);
  return locations;
};

// Type pour les services Mobile Money
export interface MobileMoneyService {
  code: string;      // ex: 'OM', 'MOMO'
  name: string;      // ex: 'Orange Money', 'MTN Mobile Money'
  isActive: boolean; // Pour activer/désactiver un service
}

// Fonction pour récupérer les services Mobile Money
export const getMobileMoneyServices = async () => {
  console.log('Début getMobileMoneyServices');
  const docRef = doc(db, 'settings', 'mobileMoneyServices');
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    // Initialisation avec les services par défaut
    const defaultServices: MobileMoneyService[] = [
      { code: 'OM', name: 'Orange Money', isActive: true },
      { code: 'MOMO', name: 'MTN Mobile Money', isActive: true }
    ];
    
    await setDoc(docRef, { services: defaultServices });
    return defaultServices;
  }
  
  return docSnap.data().services;
};

// Fonction pour vérifier les droits d'admin
export const checkAdminRights = async (userId: string) => {
  try {
    if (!userId) {
      console.log('Pas d\'ID utilisateur fourni');
      return false;
    }

    console.log('Vérification des droits admin pour:', userId);
    const adminRef = doc(db, 'drawAdmins', userId);
    const adminDoc = await getDoc(adminRef);
    
    if (!adminDoc.exists()) {
      console.log('Document admin non trouvé');
      return false;
    }

    const data = adminDoc.data();
    console.log('Données admin:', data);
    return {
      isAdmin: true,
      canInitiateDraw: data.canInitiateDraw === true,
      canSetDrawTime: data.canSetDrawTime === true
    };
  } catch (error) {
    console.error('Erreur vérification admin:', error);
    return false;
  }
};

// Fonction pour configurer un admin de tirage (mise à jour)
export const setupDrawAdmin = async (userId: string) => {
  try {
    const adminData = {
      canInitiateDraw: true,
      canSetDrawTime: true,
      createdAt: new Date(),
      userId: userId,
      isAdmin: true  // Ajout de ce champ
    };
    
    await setDoc(doc(db, 'drawAdmins', userId), adminData);
    console.log('Admin configuré avec succès:', adminData);
    return true;
  } catch (error) {
    console.error('Erreur lors de la configuration admin:', error);
    return false;
  }
};

// Ajouter cette fonction pour sauvegarder une livraison
export const saveDeliveryRequest = async (userId: string, deliveryData: any) => {
  try {
    const deliveryRef = doc(collection(db, 'users', userId, 'deliveries'));
    await setDoc(deliveryRef, {
      ...deliveryData,
      createdAt: new Date(),
      status: 'pending'
    });
    return deliveryRef.id;
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de la livraison:', error);
    throw error;
  }
};

// Après les autres interfaces
export interface UsernameChange {
  username: string;
  changedAt: Date;
}

// Ajouter cette fonction
export const getUsernameHistory = async (userId: string) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) return [];
    
    const history = userDoc.data()?.usernameHistory || [];
    
    // Trier par date décroissante (plus récent en haut)
    return history.sort((a: any, b: any) => {
      const dateA = a.changedAt instanceof Timestamp ? a.changedAt.toDate() : new Date(a.changedAt);
      const dateB = b.changedAt instanceof Timestamp ? b.changedAt.toDate() : new Date(b.changedAt);
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'historique:', error);
    return [];
  }
};

export const updateUserSearchFields = async (userId: string, username: string) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      username: username,
      usernameLower: username.toLowerCase(),
      updatedAt: new Date()
    }, { merge: true });
  } catch (error) {
    console.error('Erreur lors de la mise à jour des champs de recherche:', error);
  }
};

export const createDrawNotification = async (userId: string, drawData: any) => {
  try {
    // Création dans la collection racine 'notifications'
    const notificationRef = doc(collection(db, 'notifications'));
    
    const notificationData = {
      id: notificationRef.id,
      userId: userId,
      drawId: drawData.drawId || notificationRef.id,
      type: 'draw_win',
      amount: drawData.amount,
      createdAt: new Date(),
      status: 'pending'
    };

    await setDoc(notificationRef, notificationData);
    console.log('Notification créée dans la collection racine:', notificationData);
    return notificationRef.id;
  } catch (error) {
    console.error('Erreur lors de la création de la notification:', error);
    throw error;
  }
};

export const getProfileLikes = async (userId: string) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    return userDoc.exists() ? userDoc.data()?.likes || 0 : 0;
  } catch (error) {
    console.error('Erreur lors de la récupération des likes:', error);
    return 0;
  }
};

export const updateUsernameWithHistory = async (userId: string, newUsername: string) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const currentData = userDoc.exists() ? userDoc.data() : {};
    
    const usernameHistory = currentData.usernameHistory || [];
    
    // Utiliser Timestamp.fromDate() pour la date
    const change = {
      username: newUsername,
      changedAt: Timestamp.fromDate(new Date())
    };
    
    await setDoc(userRef, {
      username: newUsername,
      usernameLower: newUsername.toLowerCase(),
      usernameHistory: [...usernameHistory, change],
      updatedAt: Timestamp.fromDate(new Date())
    }, { merge: true });

  } catch (error) {
    console.error('Erreur lors de la mise à jour du nom d\'utilisateur:', error);
    throw error;
  }
};

// Ajouter cette nouvelle fonction pour récupérer les demandes
export const get2DRRequests = async (): Promise<ExtendedDonationRequest[]> => {
  try {
    const requestsRef = collection(db, REQUESTS_COLLECTION);
    const snapshot = await getDocs(requestsRef);
    
    return snapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          isPublic: data.isPublic || false,
          details: data.details || '',
          amount: data.amount || 0,
          donations: data.donations || [],
          likes: data.likes || 0,
          location: data.location || { city: '', country: '' },
          creatorName: data.creatorName || '',
          creatorCity: data.creatorCity || '',
          creatorCountry: data.creatorCountry || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          hasFixedAmount: data.hasFixedAmount || false,
          displayEndDate: data.displayEndDate ? new Date(data.displayEndDate) : undefined,
          displayDuration: data.displayDuration || undefined,
          uniqueDonorsCount: (data.donations || [])
            .filter((d: any) => !d.isAnonymous)
            .length
        } as ExtendedDonationRequest;
      })
      .sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
  } catch (error) {
    console.error('Erreur lors de la récupération des demandes:', error);
    return [];
  }
};

export const delete2DRRequest = async (requestId: string) => {
  try {
    await deleteDoc(doc(db, REQUESTS_COLLECTION, requestId));
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de la demande:', error);
    return false;
  }
};

// Fonction pour obtenir la limite du TOP
export const getTopLimit = async (): Promise<number> => {
  try {
    const settingsRef = doc(db, 'settings', 'topDonors');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return settingsDoc.data().limit || 100; // Par défaut 100 si non spécifié
    }
    
    // Créer le document s'il n'existe pas
    await setDoc(settingsRef, { limit: 100 });
    return 100;
  } catch (error) {
    console.error('Erreur lors de la récupération de la limite:', error);
    return 100; // Valeur par défaut en cas d'erreur
  }
};

// Modifier la fonction getTopDonors pour utiliser la limite dynamique
export const getTopDonors = async (): Promise<TopDonor[]> => {
  try {
    const limit = await getTopLimit();
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const donors = snapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          username: data.username || 'Anonyme',
          totalDonations: data.amountGiven || 0
        };
      })
      .filter(donor => donor.totalDonations > 0)
      .sort((a, b) => b.totalDonations - a.totalDonations)
      .slice(0, limit)
      .map((donor, index) => ({
        ...donor,
        rank: index + 1
      }));

    return donors;
  } catch (error) {
    console.error('Erreur lors de la récupération des meilleurs donateurs:', error);
    return [];
  }
};

export const updateUserTotalDonations = async (userId: string, amount: number) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      amountGiven: increment(amount),
      lastDonationDate: new Date()
    });
  } catch (error) {
    console.error('Erreur lors de la mise à jour du total des donations:', error);
  }
};

export const getUserRanking = async (userId: string): Promise<number | null> => {
  try {
    // Récupérer tous les utilisateurs triés par montant total de dons
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      orderBy('amountGiven', 'desc'),
      limit(1000)
    );
    
    const snapshot = await getDocs(q);
    const position = snapshot.docs.findIndex(doc => doc.id === userId) + 1;
    
    // Si l'utilisateur est dans le top 1000, retourner sa position
    // Sinon retourner null
    return position > 0 && position <= 1000 ? position : null;
  } catch (error) {
    console.error('Erreur lors de la récupération du classement:', error);
    return null;
  }
};

// Types pour les paramètres de parrainage
export interface ReferralSettings {
  isActive: boolean;
  reward: number;  // Montant en FCFA pour chaque parrainage
  maxReferrals: number;  // Nombre maximum de parrainages par utilisateur
  lastUpdated: Date;
}

// Initialiser les paramètres de parrainage
export const initializeReferralSettings = async () => {
  try {
    const settingsRef = doc(db, 'settings', 'referral');
    const settingsDoc = await getDoc(settingsRef);

    if (!settingsDoc.exists()) {
      await setDoc(settingsRef, {
        isActive: true,
        reward: 25,  // 25 FCFA par défaut
        maxReferrals: 100,
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation des paramètres de parrainage:', error);
  }
};

// Récupérer les paramètres de parrainage
export const getReferralSettings = async (): Promise<ReferralSettings | null> => {
  try {
    const settingsRef = doc(db, 'settings', 'referral');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return {
        ...settingsDoc.data(),
        lastUpdated: settingsDoc.data().lastUpdated?.toDate()
      } as ReferralSettings;
    }
    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération des paramètres de parrainage:', error);
    return null;
  }
};

// Ajouter l'interface pour les donateurs précédents
export interface PreviousDonor {
  imageUrl: string;
  name: string;
  amount: number;
  link: string;
}

// Modification de l'interface GeneralDonationData
export interface GeneralDonationData {
  id?: string;
  title: string;
  donorName: string;
  amount: number;
  imageUrl: string;
  isActive: boolean;
  description?: string;
  detailsLink?: string;
  details: string;
  alternativeLink: string;
  lastUpdated?: Date;
  previousDonors?: PreviousDonor[];
  videoUrl?: string; // URL de la vidéo YouTube à afficher
}

// Modification de l'initialisation
const initializeGeneralDonation = async () => {
  try {
    const donationRef = doc(db, 'settings', 'generalDonation');
    const donationDoc = await getDoc(donationRef);
    
    if (!donationDoc.exists()) {
      await setDoc(donationRef, {
        title: 'Donation générale',
        imageUrl: 'https://i.ibb.co/nqkdHYn/Orange-logo-svg.png',
        amount: 30000,
        description: 'Gratuit aux utilisateurs de magraine !',
        details: 'Dans le cadre de son programme....',
        detailsLink: 'https://www.youtube.com/watch?v=iZBMP2SPmQ4&list=TLPQMTU',
        isActive: false,
        lastUpdated: new Date(),
        donorName: 'MTN',
        alternativeLink: 'https://example.com/donation',
        previousDonors: [
          {
            imageUrl: 'https://i.ibb.co/nqkdHYn/Orange-logo-svg.png',
            name: 'Orange Money',
            amount: 500000,
            link: 'https://www.orange.cm'
          },
          {
            imageUrl: 'https://i.ibb.co/nqkdHYn/mtn-logo.png',
            name: 'MTN MoMo',
            amount: 300000,
            link: 'https://www.mtn.cm'
          }
        ]
      });
      console.log('Document generalDonation initialisé avec previousDonors');
    } else {
      // Mettre à jour le document existant si previousDonors n'existe pas
      const data = donationDoc.data();
      if (!data.previousDonors) {
        await updateDoc(donationRef, {
          previousDonors: [
            {
              imageUrl: 'https://i.ibb.co/nqkdHYn/Orange-logo-svg.png',
              name: 'Orange Money',
              amount: 500000,
              link: 'https://www.orange.cm'
            },
            {
              imageUrl: 'https://i.ibb.co/nqkdHYn/mtn-logo.png',
              name: 'MTN MoMo',
              amount: 300000,
              link: 'https://www.mtn.cm'
            }
          ]
        });
        console.log('previousDonors ajouté au document existant');
      }
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation de la donation générale:', error);
  }
};

// Fonction pour récupérer les données de donation générale
export const getGeneralDonationData = async (): Promise<GeneralDonationData | null> => {
  try {
    const donationRef = doc(db, 'settings', 'generalDonation');
    const donationDoc = await getDoc(donationRef);
    if (donationDoc.exists()) {
      const data = donationDoc.data();
      return {
        id: donationDoc.id,
        ...data,
        title: data.title || '',
        donorName: data.donorName || '',
        imageUrl: data.imageUrl || '',
        lastUpdated: data.lastUpdated?.toDate(),
        previousDonors: data.previousDonors || [],
        amount: Number(data.amount),
        isActive: Boolean(data.isActive),
        details: data.details || '',
        alternativeLink: data.alternativeLink || '',
        videoUrl: data.videoUrl || ''
      } as GeneralDonationData;
    }
    return null;
  } catch (error) {
    console.error('Erreur:', error);
    return null;
  }
};

// Fonction pour mettre à jour la structure de la donation générale
export const updateGeneralDonationStructure = async (donorName: string, alternativeLink: string) => {
  try {
    const donationRef = doc(db, 'settings', 'generalDonation');
    await updateDoc(donationRef, {
      donorName,
      alternativeLink
    });
    console.log('Structure de donation générale mise à jour');
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la structure:', error);
  }
};

export interface GeneralDonationClick {
  userId: string;
  donationId: string;
  timestamp: Date;
  donorName: string;
  amount: number;
}

export const recordGeneralDonationClick = async (userId: string, donationData: GeneralDonationData) => {
  try {
    console.log('Début recordGeneralDonationClick', { userId, donationData });
    
    if (!donationData.isActive || !donationData.id) {
      console.log('Donation inactive ou ID manquant', {
        isActive: donationData.isActive,
        id: donationData.id
      });
      return;
    }

    await runTransaction(db, async (transaction) => {
      // 1. Vérifier si c'est une nouvelle session de donation
      const donationRef = doc(db, 'settings', 'generalDonation');
      const donationDoc = await transaction.get(donationRef);
      const currentDonationData = donationDoc.data();

      // 2. Si la session a changé, supprimer tous les anciens clics
      if (currentDonationData?.lastUpdated?.toDate()?.getTime() !== donationData.lastUpdated?.getTime()) {
        const clicksRef = collection(db, 'generalDonationClicks');
        const oldClicks = await getDocs(clicksRef);
        oldClicks.docs.forEach(doc => {
          transaction.delete(doc.ref);
        });
      }

      // 3. Enregistrer le nouveau clic s'il n'existe pas déjà
      const clickId = `${userId}_${donationData.id}`;
      const clickRef = doc(db, 'generalDonationClicks', clickId);
      const clickDoc = await transaction.get(clickRef);

      if (!clickDoc.exists()) {
        transaction.set(clickRef, {
          userId,
          donationId: donationData.id,
          timestamp: new Date(),
          donorName: donationData.donorName,
          amount: donationData.amount,
          sessionTimestamp: donationData.lastUpdated
        });
      }
    });
  } catch (error) {
    console.error('Erreur dans recordGeneralDonationClick:', error);
  }
};

// Ajouter cette fonction pour mettre à jour le total des donations publiques
export const updateUserPublicDonations = async (userId: string, amount: number) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      amountGiven: increment(amount)
    });
  } catch (error) {
    console.error('Erreur lors de la mise à jour du total des donations:', error);
  }
};

// Ajouter cette fonction pour mettre à jour les demandes existantes
export const updateRequestSearchFields = async (requestId: string, title: string, details: string) => {
  try {
    await setDoc(doc(db, REQUESTS_COLLECTION, requestId), {
      title: title,
      titleLower: title.toLowerCase(),
      details: details,
      detailsLower: details.toLowerCase(),
      updatedAt: new Date()
    }, { merge: true });
    console.log('Champs de recherche mis à jour pour la demande:', requestId);
  } catch (error) {
    console.error('Erreur lors de la mise à jour des champs de recherche:', error);
  }
};

// Fonction pour mettre à jour toutes les demandes existantes
export const updateAllRequestsSearchFields = async () => {
  try {
    console.log('Début de la mise à jour des champs de recherche pour toutes les demandes...');
    const requestsRef = collection(db, REQUESTS_COLLECTION);
    const snapshot = await getDocs(requestsRef);
    
    let updatedCount = 0;
    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (!data.titleLower || !data.detailsLower) {
        await updateRequestSearchFields(doc.id, data.title || '', data.details || '');
        updatedCount++;
      }
    }
    
    console.log(`Mise à jour terminée. ${updatedCount} demandes ont été mises à jour.`);
    return updatedCount;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des demandes:', error);
    return 0;
  }
};